import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/common.css';
import '../../styles/test-plans.css';
import axiosInstance from '../../api/axiosInstance';
import { AccessibleSelect } from '../common';
import { RtModal, RtModalField, RtModalSection } from '../ui/RtModal';
import {
  MAX_TEST_EXECUTION_NAME_LENGTH,
  validateTestExecutionName,
} from './testExecutionValidation';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const CreateTestPlanModal = ({
  onClose,
  onSave,
  selectedEntity,
  activeTab,
  projectName,
  projectId,
  releaseId,
  initialEnvironments = [],
  initialMembers = [],
}) => {
  const [testPlan, setTestPlan] = useState({
    name: '',
    description: '',
    priority: activeTab === 'epics' ? 'high' : 'medium',
    estimatedDuration: '',
    assignee: '',
    status: 'draft',
    environment: '',
  });

  const [errors, setErrors] = useState({});
  const [environments, setEnvironments] = useState(initialEnvironments);
  const [environmentsLoading, setEnvironmentsLoading] = useState(
    initialEnvironments.length === 0
  );
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [members, setMembers] = useState(initialMembers);

  const environmentOptions = useMemo(
    () =>
      environments.map((env) => ({
        value: String(env.id),
        label: env.environment_name,
      })),
    [environments]
  );

  const assigneeOptions = useMemo(
    () =>
      members.map((member) => ({
        value: String(member.user_id),
        label: member.name,
      })),
    [members]
  );

  useEffect(() => {
    setEnvironments(initialEnvironments);
    setMembers(initialMembers);
    setEnvironmentsLoading(initialEnvironments.length === 0);
  }, [initialEnvironments, initialMembers]);

  useEffect(() => {
    if (!projectId) return;

    if (initialEnvironments.length === 0) {
      fetchEnvironments();
    }
    if (initialMembers.length === 0) {
      fetchMembers();
    }
  }, [projectId, initialEnvironments.length, initialMembers.length]);

  const fetchEnvironments = async () => {
    try {
      setEnvironmentsLoading(true);
      const response = await axiosInstance.get(
        `/user_profile/list_environments?project_id=${projectId}`
      );

      if (response.data && response.status_code === 200) {
        setEnvironments(response.data || []);
      } else {
        console.error('Failed to fetch environments:', response);
        setEnvironments([]);
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      setEnvironments([]);
    } finally {
      setEnvironmentsLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axiosInstance.get(
        `/projects/list_project_members?project_id=${projectId}`
      );

      if (response.data && response.status === 200) {
        setMembers(response.data || []);
      } else {
        console.error('Failed to fetch environments:', response);
        setMembers([]);
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      setMembers([]);
    } finally {
      // setEnvironmentsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleFieldChange = (name, value) => {
    setTestPlan((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'environment') {
      const selected = environments.find(
        (env) => String(env.id) === String(value)
      );
      setSelectedEnvironment(selected || null);
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const nameError = validateTestExecutionName(testPlan.name);
    if (nameError) {
      newErrors.name = nameError;
    }

    if (!testPlan.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (activeTab === 'releases' && !testPlan.estimatedDuration) {
      newErrors.estimatedDuration = 'Duration is required for releases';
    }

    if (!testPlan.environment) {
      newErrors.environment = 'Environment is required';
    }

    if (!testPlan.assignee) {
      newErrors.assignee = 'Assignee is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      project_id: projectId,
      release_id: releaseId,
      name: testPlan.name,
      description: testPlan.description,
      status: testPlan.status,
      priority: testPlan.priority,
      assignee: testPlan.assignee,
      duration: parseInt(testPlan.estimatedDuration) || 0,
      environment_setting_id: parseInt(testPlan.environment) || null,
    };

    if (selectedEntity) {
      if (activeTab === 'releases') {
        payload.release_id = selectedEntity.id;
      } else if (activeTab === 'sprints') {
        payload.sprint_id = selectedEntity.sprint_id || selectedEntity.id;
      } else if (activeTab === 'epics') {
        payload.epic_id = selectedEntity.id;
      }
    }

    try {
      await onSave(payload);
    } catch (error) {
      if (error?.fieldErrors) {
        setErrors((prev) => ({ ...prev, ...error.fieldErrors }));
      }
    }
  };

  const entityLabel =
    activeTab?.slice(0, -1).charAt(0).toUpperCase() + activeTab?.slice(0, -1).slice(1);
  const entityDisplay =
    selectedEntity &&
    (activeTab === 'epics' ? selectedEntity.key : selectedEntity.name);

  return (
    <RtModal
      open
      onClose={onClose}
      title={`Create Test Execution for ${activeTab?.slice(0, -1)}${
        selectedEntity ? `: ${entityDisplay}` : ''
      }`}
      size="lg"
      footer={
        <>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="rt-create-test-plan-form" className="rt-btn rt-btn--primary">
            Create Test Execution
          </button>
        </>
      }
    >
      <form id="rt-create-test-plan-form" onSubmit={handleSubmit}>
        <RtModalSection title="Execution details">
          <RtModalField
            label="Test Execution Name"
            htmlFor="name"
            required
            error={errors.name || null}
            hint={!errors.name ? `Maximum ${MAX_TEST_EXECUTION_NAME_LENGTH} characters allowed.` : null}
          >
            <input
              type="text"
              id="name"
              name="name"
              value={testPlan.name}
              onChange={handleChange}
              maxLength={MAX_TEST_EXECUTION_NAME_LENGTH}
              className={errors.name ? 'error' : ''}
              placeholder="Enter test execution name"
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'name-error' : 'name-hint'}
            />
          </RtModalField>

          <RtModalField
            label="Description"
            htmlFor="description"
            required
            error={errors.description || null}
          >
            <textarea
              id="description"
              name="description"
              value={testPlan.description}
              onChange={handleChange}
              className={errors.description ? 'error' : ''}
              placeholder="Enter test execution description"
              rows="4"
              aria-invalid={Boolean(errors.description)}
              aria-describedby={errors.description ? 'description-error' : undefined}
            />
          </RtModalField>
        </RtModalSection>

        <RtModalSection title="Scheduling & environment">
          <div className="rt-modal__grid-3">
            <RtModalField label="Priority" htmlFor="priority">
              <AccessibleSelect
                id="priority"
                value={testPlan.priority}
                onChange={(value) => handleFieldChange('priority', value)}
                options={PRIORITY_OPTIONS}
                placeholder="Select Priority"
                aria-labelledby="priority-label"
              />
            </RtModalField>

            <RtModalField
              label="Est. Duration (days)"
              htmlFor="estimatedDuration"
              required={activeTab === 'releases'}
              error={errors.estimatedDuration || null}
            >
              <input
                type="number"
                id="estimatedDuration"
                name="estimatedDuration"
                value={testPlan.estimatedDuration}
                onChange={handleChange}
                min="1"
                className={errors.estimatedDuration ? 'error' : ''}
                placeholder="Enter days"
                required={activeTab === 'releases'}
                aria-invalid={Boolean(errors.estimatedDuration)}
              />
            </RtModalField>

            <RtModalField
              label="Environment"
              htmlFor="environment"
              required
              error={errors.environment || null}
              hint={
                selectedEnvironment
                  ? `URL: ${selectedEnvironment.target_url}`
                  : null
              }
            >
              <AccessibleSelect
                id="environment"
                value={testPlan.environment}
                onChange={(value) => handleFieldChange('environment', value)}
                options={environmentOptions}
                placeholder={environmentsLoading ? 'Loading...' : 'Select Environment'}
                disabled={environmentsLoading}
                required
                error={Boolean(errors.environment)}
                aria-labelledby="environment-label"
              />
            </RtModalField>
          </div>

          <RtModalField
            label="Assignee"
            htmlFor="assignee"
            required
            error={errors.assignee || null}
          >
            <AccessibleSelect
              id="assignee"
              value={testPlan.assignee}
              onChange={(value) => handleFieldChange('assignee', value)}
              options={assigneeOptions}
              placeholder="Select Assignee"
              required
              error={Boolean(errors.assignee)}
              aria-labelledby="assignee-label"
            />
          </RtModalField>
        </RtModalSection>

        <div className="rt-modal-meta">
          <p>
            <strong>Project:</strong> {projectName}
          </p>
          {selectedEntity ? (
            <p>
              <strong>{entityLabel}:</strong> {entityDisplay}
            </p>
          ) : null}
        </div>
      </form>
    </RtModal>
  );
};

export default CreateTestPlanModal;
